/**
 * ROOF BEARING WALLS (2026-10-05, Matt: "the bearing line is drawn on the interior face
 * of the stud, not the exterior. The roof outline should sense the thickness of the
 * wall that it's bearing on and flex to accommodate the wall thickness. This should
 * also apply for outlookers, barge rafters, and general rafters on the rake side").
 *
 * For each edge of a roof's bearing boundary: how far the wall standing under that edge
 * reaches OUTSIDE the bearing line — the distance from the line to the wall's outer face
 * of STUD (the core), never its finishes. The roof solver adds it to the edge's overhang
 * (so an overhang is measured from the outside face of the framing, and a zero eave stops
 * there) and the show members cap their decorative cuts to the part past the wall.
 *
 * FACE-OF-STUD fix (2026-10-12, Matt): this used to reach to the wall's exterior FINISH
 * face (sheathing, siding, painted cladding), so a zero eave ran out over the siding.
 *
 * Matching is geometric unless the edge says otherwise: by default an edge bears on the
 * carrying wall whose nearest stud face sits on the drawn line (BEARS ON, 2026-10-13 — see
 * roofBearingWalls); an edge can name its wall, or 'none'. 0 where no wall bears.
 */
import type { ModelDoc } from './doc'
import { listElements, resolveElement } from './doc'
import type { ElementId, Path2D, Pt2 } from './types'
import { flattenPathPts } from './arc'
import { centerlineOffsetFt, referenceOffsets } from './locationLine'

const MAX_FT = 2.5        // a wall can't plausibly reach further than this past the line
const NEAR_FT = 3         // centerline distance from the bearing line to consider a wall
const PARALLEL = 0.05     // |sin| between the wall and the edge

export function roofBearingWallFt(model: ModelDoc, ring: Pt2[], zPlateFt: number, roofId: string): number[] {
  return roofBearingWalls(model, ring, zPlateFt, roofId).ft
}

/** what a roof edge was told to bear on (`node.edge.bearingWall`): blank = auto, 'none',
 *  or a wall id the user picked */
export type BearingChoice = string | null | undefined

/** how an edge's bearing wall was decided */
export type BearingMode = 'auto' | 'picked' | 'none'

/** the stud face nearest the drawn line may sit this far off it and still be "the wall the
 *  line is drawn on" (a line drawn a hair inside the stud, or on the sheathing, still counts;
 *  a wall a few inches over — the next wall of a stepped run — does not) */
const ON_LINE_FT = 4 / 12

interface WallRec {
  id: string; pts: Pt2[]; t: number; delta: number; coreExt: number; coreInt: number
  flipped: boolean; carries: boolean
}

/** one wall segment measured against one roof edge, all distances OUTWARD from the line */
interface Fit { id: string; inner: number; outer: number; finish: number; overlap: number }

function fitWall(w: WallRec, a: Pt2, d: Pt2, o: Pt2, L: number): Fit | null {
  let best: Fit | null = null
  for (let k = 0; k + 1 < w.pts.length; k++) {
    const p = w.pts[k], q = w.pts[k + 1]
    const wl = Math.hypot(q[0] - p[0], q[1] - p[1])
    if (wl < 1e-6) continue
    const wd: Pt2 = [(q[0] - p[0]) / wl, (q[1] - p[1]) / wl]
    if (Math.abs(wd[0] * d[1] - wd[1] * d[0]) > PARALLEL) continue
    // overlap along the edge
    const tp = (p[0] - a[0]) * d[0] + (p[1] - a[1]) * d[1]
    const tq = (q[0] - a[0]) * d[0] + (q[1] - a[1]) * d[1]
    const lo = Math.max(0, Math.min(tp, tq)), hi = Math.min(L, Math.max(tp, tq))
    if (hi - lo < Math.min(1, L * 0.3)) continue
    // the wall's drawn line, its centerline and its faces, measured OUTWARD
    const mid: Pt2 = [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2]
    const off = (mid[0] - a[0]) * o[0] + (mid[1] - a[1]) * o[1]
    const nW: Pt2 = [-wd[1], wd[0]]                  // the wall's left normal
    const s = nW[0] * o[0] + nW[1] * o[1]            // ±1: left normal vs outward
    const center = off + s * w.delta
    if (Math.abs(center) > NEAR_FT) continue
    // the wall's exterior side, as ±1 along outward (exterior = left normal unless flipped)
    const e = w.flipped ? -s : s
    const f1 = e * w.coreExt, f2 = e * w.coreInt
    const fit: Fit = {
      id: w.id, inner: center + Math.min(f1, f2), outer: center + Math.max(f1, f2),
      finish: center + w.t / 2, overlap: hi - lo,
    }
    if (!best || Math.abs(fit.inner) < Math.abs(best.inner)) best = fit
  }
  return best
}

/**
 * PLAN PERF round (2026-10-06): the per-edge offsets AND the ids of the walls that
 * produced them. `ft` reaches the outside face of STUD (what the roof edge and its
 * overhang count from); `faceFt` reaches the wall's finished outside face (what show
 * members that sit AGAINST the wall — frieze, corbels, soffit, tail cuts — butt to). The
 * geometry worker's dirty closure reads `ids` off the resolved roof
 * (`ResolvedElement.roofBearingWallIds`) so that moving a nearby wall re-derives the roof.
 *
 * BEARS ON (2026-10-13, Matt): an edge bears on ONE wall — the one whose stud face nearest
 * the line sits on the drawn line (within ON_LINE_FT). It used to take the FARTHEST wall
 * within 3', so at a stepped wall it grabbed the next step's wall and over-extended the eave
 * (Santiago ecOi side 2: +1'-8" instead of 5 1/2"). Each edge can also be told what it bears
 * on (`choices[i]`, the boundary node's `edge.bearingWall`): blank = auto, 'none' = the
 * overhang counts from the drawn line, or a picked wall id (any wall parallel to the edge,
 * overlapping it and within 3' — a picked wall that's gone or moved away falls back to auto).
 */
export function roofBearingWalls(
  model: ModelDoc, ring: Pt2[], zPlateFt: number, roofId: string, choices: BearingChoice[] = [],
): { ft: number[]; faceFt: number[]; ids: string[]; walls: (string | null)[]; modes: BearingMode[] } {
  const n = ring.length
  const out = new Array<number>(n).fill(0)
  const faceOut = new Array<number>(n).fill(0)
  const edgeWalls = new Array<string | null>(n).fill(null)
  const modes = new Array<BearingMode>(n).fill('auto')
  const ids = new Set<string>()
  if (n < 3) return { ft: out, faceFt: faceOut, ids: [], walls: edgeWalls, modes }
  let area = 0
  for (let i = 0; i < n; i++) {
    const a = ring[i], b = ring[(i + 1) % n]
    area += a[0] * b[1] - b[0] * a[1]
  }
  const ccw = area > 0

  const walls: WallRec[] = []
  const picked = new Set(choices.filter((c): c is string => typeof c === 'string' && c !== '' && c !== 'none' && c !== 'auto'))
  for (const rec of listElements(model)) {
    if (rec.category !== 'wall') continue
    const w = resolveElement(model, rec.id as ElementId)
    if (!w?.level || !w.params.path) continue
    const zBase = w.level.elevationFt + ((w.params.baseOffsetFt as number) || 0)
    // walls that could carry this plate: based at or below it, not absurdly far below;
    // and only walls that CARRY the roof: attached to it (a gable wall under a rake), or
    // topping out at its plate — a parapet rising past the plate stands beside the roof,
    // never under it. A PICKED wall skips these tests: the user said so.
    let carries = !(zBase > zPlateFt + 0.5 || zBase < zPlateFt - 40)
    if (carries) {
      const aIds = Array.isArray(w.params.topAttachIds) ? (w.params.topAttachIds as string[]) : []
      const attached = aIds.includes(roofId) || (!aIds.length && w.params.topAttach === 'roof')
      if (!attached) {
        const topLv = w.params.topMode === 'level' ? w.paramLevels?.topLevelId : null
        const zTop = topLv
          ? topLv.elevationFt + ((w.params.topOffsetFt as number) || 0)
          : zBase + ((w.params.unconnectedHeightFt as number) || 9)
        if (zTop > zPlateFt + 1 || zTop < zPlateFt - 2) carries = false
      }
    }
    if (!carries && !picked.has(rec.id as string)) continue
    const pts = flattenPathPts(w.params.path as Path2D)
    if (pts.length < 2) continue
    const t = w.assemblyThicknessFt || 0
    if (!(t > 0)) continue
    const delta = centerlineOffsetFt(w.params.locationLine as never, w.params.flipped as never, w.assembly)
    // the core's two faces from the centerline, measured toward the wall's EXTERIOR side
    // (layers run exterior → interior; the centerline sits at T/2 from the exterior face)
    const ro = referenceOffsets(w.assembly)
    const T = ro.finishFaceInterior
    walls.push({
      id: rec.id as string, pts, t, delta,
      coreExt: T / 2 - ro.coreFaceExterior, coreInt: T / 2 - ro.coreFaceInterior,
      flipped: !!w.params.flipped, carries,
    })
  }

  for (let i = 0; i < n; i++) {
    const choice = choices[i]
    if (choice === 'none') { modes[i] = 'none'; continue }
    const a = ring[i], b = ring[(i + 1) % n]
    const L = Math.hypot(b[0] - a[0], b[1] - a[1])
    if (L < 1e-6) continue
    const d: Pt2 = [(b[0] - a[0]) / L, (b[1] - a[1]) / L]
    // outward = right of travel on a CCW ring, left on a CW one
    const o: Pt2 = ccw ? [d[1], -d[0]] : [-d[1], d[0]]
    let use: Fit | null = null
    if (typeof choice === 'string' && choice && choice !== 'auto') {
      const w = walls.find((x) => x.id === choice)
      const fit = w ? fitWall(w, a, d, o, L) : null
      if (fit) { use = fit; modes[i] = 'picked'; ids.add(fit.id) }
    }
    if (!use) {
      // auto: the carrying wall the line is drawn on
      for (const w of walls) {
        if (!w.carries) continue
        const fit = fitWall(w, a, d, o, L)
        if (!fit) continue
        ids.add(fit.id)   // every nearby parallel wall: moving one onto the line re-derives
        if (Math.abs(fit.inner) > ON_LINE_FT) continue
        if (!use || Math.abs(fit.inner) < Math.abs(use.inner) - 1e-6
          || (Math.abs(Math.abs(fit.inner) - Math.abs(use.inner)) <= 1e-6 && fit.overlap > use.overlap)) use = fit
      }
    }
    if (typeof choice === 'string' && choice && choice !== 'auto') ids.add(choice)
    if (!use) continue
    edgeWalls[i] = use.id
    out[i] = Math.min(MAX_FT, Math.max(0, use.outer))
    faceOut[i] = Math.min(MAX_FT, Math.max(out[i], use.finish))
  }
  return { ft: out, faceFt: faceOut, ids: [...ids], walls: edgeWalls, modes }
}

/**
 * BEARS ON picker (2026-10-13): the walls one roof edge could bear on — every wall parallel
 * to it, overlapping it and within 3' (no height test: the user is choosing), nearest the
 * line first, with how far its nearest stud face sits from the line (+ = outside).
 */
export function roofEdgeBearingCandidates(model: ModelDoc, ring: Pt2[], edgeIndex: number): { id: string; innerFt: number; outerFt: number }[] {
  const n = ring.length
  if (n < 3 || edgeIndex < 0 || edgeIndex >= n) return []
  let area = 0
  for (let i = 0; i < n; i++) { const a = ring[i], b = ring[(i + 1) % n]; area += a[0] * b[1] - b[0] * a[1] }
  const a = ring[edgeIndex], b = ring[(edgeIndex + 1) % n]
  const L = Math.hypot(b[0] - a[0], b[1] - a[1])
  if (L < 1e-6) return []
  const d: Pt2 = [(b[0] - a[0]) / L, (b[1] - a[1]) / L]
  const o: Pt2 = area > 0 ? [d[1], -d[0]] : [-d[1], d[0]]
  const out: { id: string; innerFt: number; outerFt: number }[] = []
  for (const rec of listElements(model)) {
    if (rec.category !== 'wall') continue
    const w = resolveElement(model, rec.id as ElementId)
    if (!w?.level || !w.params.path || !(w.assemblyThicknessFt > 0)) continue
    const pts = flattenPathPts(w.params.path as Path2D)
    if (pts.length < 2) continue
    const ro = referenceOffsets(w.assembly)
    const T = ro.finishFaceInterior
    const fit = fitWall({
      id: rec.id as string, pts, t: w.assemblyThicknessFt, flipped: !!w.params.flipped, carries: true,
      delta: centerlineOffsetFt(w.params.locationLine as never, w.params.flipped as never, w.assembly),
      coreExt: T / 2 - ro.coreFaceExterior, coreInt: T / 2 - ro.coreFaceInterior,
    }, a, d, o, L)
    if (fit) out.push({ id: fit.id, innerFt: fit.inner, outerFt: fit.outer })
  }
  return out.sort((x, y) => Math.abs(x.innerFt) - Math.abs(y.innerFt))
}
